// libraries.
import { toast } from 'react-toastify';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// selectors.
import {
  getCountry,
  getEmail,
  setRoles,
  clearRoles,
} from '@domains/user/store/slices/user-slice/user-slice';

// endpoints.
import {
  DocRolesEnum,
  useGetDocReviewRolesQuery,
} from '@domains/user/store/api-slices/roles-documents.endpoints';
import {
  RolesPolynatorEnum,
  useGetRolesPolynatorQuery,
} from '@domains/user/store/api-slices/roles-polynator.endpoint';

export const useRoleForTools = (): { isLoading: boolean } => {
  const dispatch = useDispatch();

  const email = useSelector(getEmail);
  const country = useSelector(getCountry)!;

  const prevCountry = useRef<string | undefined>(undefined);
  const prevSuccessPolynator = useRef(false);
  const prevSuccessDoc = useRef(false);

  useEffect(() => {
    if (prevCountry.current !== undefined && prevCountry.current !== country) {
      dispatch(clearRoles());
      prevSuccessDoc.current = false;
      prevSuccessPolynator.current = false;
    }
    prevCountry.current = country;
  }, [country, dispatch]);

  const canFetch = !!(country && email);

  const {
    data: docRoleData,
    isLoading: isLoadingDoc,
    isSuccess: isSuccessDoc,
    isError: isErrorDoc,
  } = useGetDocReviewRolesQuery({ country, email }, { skip: !canFetch });

  const {
    data: polynatorRoleData,
    isLoading: isLoadingPolynator,
    isSuccess: isSuccessPolynator,
    isError: isErrorPolynator,
  } = useGetRolesPolynatorQuery({ country, email }, { skip: !canFetch });

  const docRole = docRoleData?.role;
  const polynatorRole = polynatorRoleData?.role;

  useEffect(() => {
    if (docRole) dispatch(setRoles(docRole));
    if (polynatorRole) dispatch(setRoles(polynatorRole));
  }, [docRole, polynatorRole, dispatch, country]);

  useEffect(() => {
    if (isLoadingDoc || isLoadingPolynator) {
      toast.loading('Validando Rol..', { toastId: 'RoleForTool' });
    } else {
      toast.dismiss('RoleForTool');
    }
  }, [isLoadingDoc, isLoadingPolynator]);

  useEffect(() => {
    const justSucceededDoc = isSuccessDoc && !prevSuccessDoc.current;
    const justSucceededPolynator = isSuccessPolynator && !prevSuccessPolynator.current;

    if (justSucceededDoc && docRole === DocRolesEnum.DocumentReviewer) {
      toast.success('Rol revisor de documentos', { toastId: 'role-doc-success' });
    }
    if (justSucceededPolynator) {
      const isCreator = polynatorRole === RolesPolynatorEnum.PolygonCreator;
      const roleMessage = isCreator ? 'creador de polígonos' : 'validador de polígonos';
      toast.success(`Rol ${roleMessage}`, { toastId: 'role-polynator-success' });
    }

    prevSuccessDoc.current = isSuccessDoc;
    prevSuccessPolynator.current = isSuccessPolynator;
  }, [isSuccessDoc, isSuccessPolynator, docRole, polynatorRole]);

  useEffect(() => {
    if (isErrorDoc || isErrorPolynator) {
      toast.error('Error al obtener el rol, contacte a sistemas :V');
    }
  }, [isErrorDoc, isErrorPolynator]);

  return { isLoading: isLoadingDoc || isLoadingPolynator };
};
